import { R_ENTRY, R_PLANET, TAU } from '../constants';
import { angleDelta, angleDist, inArc, norm } from '../polar';
import { acquireEffect, acquireProjectile, pushEvent, slotRadius, slotTheta } from '../world';
import type {
  Content,
  Enemy,
  Slot,
  StructureDef,
  StructureTier,
  World,
} from '../types';
import { damageEnemy, killEnemy, splashDamage } from './damage';

/** A target is only valid once it's on-screen and visible. */
function targetable(e: Enemy, fromOrbit: boolean): boolean {
  if (!e.alive) return false;
  if (e.phase === 'enter' || e.phase === 'dead') return false;
  if (e.cloaked && !e.revealed) return false;
  // Burrowers pass beneath both orbital rings; orbital hardware cannot see them.
  if (fromOrbit && e.underRing) return false;
  return true;
}

/**
 * Coverage is an arc plus a range band — two float comparisons per enemy,
 * which is why 300 entities fit in the frame budget without a quadtree (§4.1).
 */
function findTarget(
  world: World,
  theta: number,
  rMin: number,
  rMax: number,
  spread: number,
  fromOrbit: boolean,
): Enemy | null {
  let best: Enemy | null = null;
  let bestR = Infinity;
  for (const e of world.enemies) {
    if (!targetable(e, fromOrbit)) continue;
    if (e.r < rMin || e.r > rMax) continue;
    if (spread < TAU && !inArc(e.theta, theta, spread)) continue;
    // Most urgent target = closest to the crust.
    if (e.r < bestR) {
      bestR = e.r;
      best = e;
    }
  }
  return best;
}

function damageMult(world: World, content: Content, slot: Slot): number {
  let mult =
    slot.layer === 'surface'
      ? world.passive.surfaceDamageMult ?? 1
      : world.passive.ringDamageMult ?? 1;

  // Damage auras stack multiplicatively across all mounted support structures.
  for (const other of world.slots) {
    const st = other.structure;
    if (!st || other.id === slot.id) continue;
    const def = content.structures.get(st.defId);
    if (def?.support !== 'damage-aura') continue;
    const tier = def.tiers[st.tier];
    if (!tier) continue;
    const a = slotTheta(world, other);
    const b = slotTheta(world, slot);
    if (angleDist(a, b) <= (tier.auraRadius ?? 0.9)) mult *= tier.auraMult ?? 1.15;
  }
  return mult;
}

export function updateStructures(world: World, content: Content, dt: number): void {
  for (const slot of world.slots) {
    const st = slot.structure;
    if (!st) continue;
    const def = content.structures.get(st.defId);
    if (!def) continue;
    const tier = def.tiers[st.tier];
    if (!tier) continue;

    const theta = slotTheta(world, slot);
    const baseR = slotRadius(world, slot);
    const fromOrbit = slot.layer !== 'surface';

    st.spin += dt * (def.kind === 'moon' ? 0.5 / Math.max(tier.mass ?? 1, 0.2) : 1.6);

    switch (def.kind) {
      case 'moon':
        moonSweep(world, content, tier, theta, baseR, dt);
        break;
      case 'mine':
        mineCheck(world, content, slot, def, tier, theta, baseR);
        break;
      case 'support':
        supportTick(world, content, def, tier, dt);
        break;
      default:
        fireTick(world, content, slot, def, tier, theta, baseR, fromOrbit, dt);
        break;
    }
  }
}

function fireTick(
  world: World,
  content: Content,
  slot: Slot,
  def: StructureDef,
  tier: StructureTier,
  theta: number,
  baseR: number,
  fromOrbit: boolean,
  dt: number,
): void {
  const st = slot.structure!;
  st.cooldown -= dt;
  if (st.cooldown > 0) return;

  // Ring guns reach a little inward as well as out, so a target that slips
  // past the ring is still briefly engaged.
  const rMin = fromOrbit ? baseR - 40 : baseR;
  const rMax = Math.min(baseR + tier.range, R_ENTRY);

  const target = findTarget(world, theta, rMin, rMax, tier.spread, fromOrbit);
  if (!target) return;

  st.cooldown = 1 / Math.max(tier.rate, 0.01);
  const dmg = tier.damage * damageMult(world, content, slot);

  if (def.kind === 'beam' || tier.muzzle <= 0) {
    // Hitscan. Beams are the answer to high-HP single targets.
    damageEnemy(world, content, target, dmg, theta);
    if (tier.splash > 0) {
      splashDamage(world, content, target.theta, target.r, tier.splash, dmg * 0.5);
    }
    const fx = acquireEffect(world, 'impact');
    if (fx) {
      fx.theta = theta;
      fx.r = baseR;
      fx.front = target.r;
      fx.spread = angleDelta(theta, target.theta);
      fx.duration = 0.12;
    }
    return;
  }

  const p = acquireProjectile(world);
  if (!p) return;
  p.alive = true;
  p.theta = theta;
  p.r = baseR;
  p.target = target.id;
  p.damage = dmg;
  p.splash = tier.splash;
  p.life = 2.5;
  p.kind = def.kind === 'turret' && tier.splash > 0 ? 'flak' : 'bolt';
  // Lead is unnecessary at these speeds; projectiles steer each tick instead.
  p.vR = tier.muzzle;
  p.vTheta = 0;
}

/**
 * A moon is a body, not a gun. It deletes swarms and barely scratches a
 * cruiser, and the choice of size is the choice between coverage and impact.
 */
function moonSweep(
  world: World,
  content: Content,
  tier: StructureTier,
  theta: number,
  baseR: number,
  dt: number,
): void {
  const arc = tier.moonArc ?? 0.18;
  const band = 26;
  for (const e of world.enemies) {
    if (!e.alive || e.phase === 'enter' || e.phase === 'dead') continue;
    if (e.underRing) continue;
    if (Math.abs(e.r - baseR) > band + e.def.size * 0.5) continue;
    if (angleDist(e.theta, theta) > arc) continue;
    damageEnemy(world, content, e, tier.damage * dt, theta);
  }
}

function mineCheck(
  world: World,
  content: Content,
  slot: Slot,
  def: StructureDef,
  tier: StructureTier,
  theta: number,
  baseR: number,
): void {
  const st = slot.structure!;
  for (const e of world.enemies) {
    if (!targetable(e, slot.layer !== 'surface')) continue;
    if (Math.abs(e.r - baseR) > 22) continue;
    if (angleDist(e.theta, theta) > tier.spread * 0.5) continue;

    splashDamage(world, content, e.theta, e.r, tier.splash, tier.damage);
    const fx = acquireEffect(world, 'impact');
    if (fx) {
      fx.theta = theta;
      fx.r = baseR;
      fx.front = baseR;
      fx.spread = 0;
      fx.duration = 0.25;
    }
    st.charges--;
    if (st.charges <= 0) {
      slot.structure = null;
      pushEvent(world, { type: 'sell', id: def.id, theta, r: baseR });
    }
    return;
  }
}

function supportTick(
  world: World,
  content: Content,
  def: StructureDef,
  tier: StructureTier,
  dt: number,
): void {
  if (def.support === 'core-repair' && world.coreHp < world.coreMax) {
    world.coreHp = Math.min(world.coreMax, world.coreHp + (tier.damage || 1) * dt);
  }
  void content;
}

export function updateProjectiles(world: World, content: Content, dt: number): void {
  for (const p of world.projectiles) {
    if (!p.alive) continue;
    p.life -= dt;
    if (p.life <= 0) {
      p.alive = false;
      continue;
    }

    const target = p.target >= 0 ? world.enemies[p.target] : undefined;
    if (target && target.alive) {
      // Steer: close the angular gap while travelling along r.
      const dTheta = angleDelta(p.theta, target.theta);
      const turn = Math.min(Math.abs(dTheta), 4.5 * dt) * Math.sign(dTheta);
      p.theta = norm(p.theta + turn);
      const dir = Math.sign(target.r - p.r) || 1;
      p.r += dir * p.vR * dt;

      const reach = target.def.size * 0.5 + 8;
      if (Math.abs(p.r - target.r) <= reach && angleDist(p.theta, target.theta) <= reach / Math.max(p.r, 1)) {
        damageEnemy(world, content, target, p.damage, p.theta);
        if (p.splash > 0) {
          splashDamage(world, content, p.theta, p.r, p.splash, p.damage * 0.6);
        }
        const fx = acquireEffect(world, 'impact');
        if (fx) {
          fx.theta = p.theta;
          fx.r = p.r;
          fx.front = p.r;
          fx.spread = 0;
          fx.duration = 0.14;
        }
        p.alive = false;
        continue;
      }
    } else {
      // Target died mid-flight: carry on outward and expire.
      p.r += p.vR * dt;
      p.theta = norm(p.theta + p.vTheta * dt);
    }

    if (p.r < R_PLANET - 20 || p.r > R_ENTRY) p.alive = false;
  }
}

/** Swarm Deploy satellites: autonomous, temporary, and slightly unruly. */
export function updateDrones(world: World, content: Content, dt: number): void {
  for (const d of world.drones) {
    if (!d.alive) continue;
    d.life -= dt;
    if (d.life <= 0) {
      d.alive = false;
      continue;
    }
    d.theta = norm(d.theta + d.vTheta * dt);
    d.cooldown -= dt;
    if (d.cooldown > 0) continue;

    const target = findTarget(world, d.theta, d.r - 60, d.r + 150, 1.6, false);
    if (!target) continue;
    d.cooldown = 0.42;

    const p = acquireProjectile(world);
    if (!p) continue;
    p.alive = true;
    p.theta = d.theta;
    p.r = d.r;
    p.target = target.id;
    p.damage = d.damage;
    p.splash = 0;
    p.life = 2;
    p.kind = 'drone';
    p.vR = 460;
    p.vTheta = 0;
  }
}

/** Contact kills for moons that touch something with no HP left to remove. */
export function sweepDead(world: World, content: Content): void {
  for (const e of world.enemies) {
    if (e.alive && e.hp <= 0) killEnemy(world, content, e, true);
  }
}
